import React from 'react'
import clsx from 'clsx'
import { Prompt } from 'react-router'
import { Maximize, Maximize4, SaveAdd, Trash } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import AllTaskTableStateProvider, {
  NAME
} from '@SonarCX/providers/AllTaskTableStateProvider'

import TaskTable from '../../TaskTable'
import Table from '@SonarCX/components/molecules/Table'
import Filters from './Filters'
import FieldFilters from '../../../Customers/FieldFilters'
import FieldPills, {
  AdvancedValue
} from '../../../Customers/FieldFilters/FieldPills'
import Views from './Filters/Views'
import TasksFilterModal from '@SonarCX/components/molecules/TasksFilterModal'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

import { parseSorts } from './helpers/parse'
import {
  ALL_TAB,
  TASK_FIELD_LIMIT
} from '@SonarCX/providers/TaskPageStateProvider'
import {
  selectAllTasksTable,
  setPaginationPage,
  setPaginationSize,
  setSort,
  clearSelected
} from '@SonarCX/slices/allTasksTable'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useTaskAPI, CALLBACK_PHASES } from '@SonarCX/providers/TaskAPIProvider'
import { useTaskSWRMutateCache } from '@SonarCX/providers/TaskSWRMutateCache'
import { useTaskComments } from '@SonarCX/providers/TaskCommentsProvider'
import { useMenuVisibility } from '@SonarCX/providers/MenuVisibilityProvider'
import { useTaskFilters } from '@SonarCX/providers/TaskFiltersProvider'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useTaskIncludes from '../useTaskIncludes'
import usePaginatedFetch from '@SonarCX/hooks/usePaginatedFetch'
import useBaseStyles from '../styles'
import useStyles from './styles'

const CUSTOM_FIELDS_VALUE_STORAGE_KEY = 'task_custom_field_values'
export const TASK_SEARCH_STORAGE_KEY = 'task_search'
const TABLE_ID = 'all-tasks-table'

const AllTasksTab = ({ pageRef, tabState }) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { sort, customSort, tableApiSort } = useAppSelector(selectAllTasksTable)

  const { isSmall } = useBreakpoint()
  const { updateCacheFromSWR, registerCallback } = useTaskAPI()
  const { setMutator } = useTaskSWRMutateCache()
  const { updateCounts } = useTaskComments()
  const { show: showMenu, hide: hideMenu } = useMenuVisibility()
  const { state, count, saveFilter, clearAll, setIncludeSubtasks } =
    useTaskFilters()

  const [tempSearch, setTempSearch] = useSavedValue<string>({
    key: TASK_SEARCH_STORAGE_KEY,
    defaultValue: '""'
  })
  const [url, setURL] = React.useState(null)
  const [tableSort, setTableSort] = React.useState([])
  const [showTabs, setShowTabs] = tabState

  const [fieldValues, setFieldValues] = useSavedValue<AdvancedValue[]>({
    key: CUSTOM_FIELDS_VALUE_STORAGE_KEY,
    defaultValue: '[]',
    loadTransform: val => val.slice(0, TASK_FIELD_LIMIT)
  })

  const allIncludes = useTaskIncludes({ key: NAME })
  const {
    data: allTasks,
    loaded,
    ...pagination
  } = usePaginatedFetch(() => {
    if (!url) {
      return null
    }

    return `allTasks:${url}${allIncludes}`
  })

  const [showModal, setShowModal] = React.useState(false)

  const showSave = React.useMemo(() => {
    return count + fieldValues.length + tempSearch.length > 0
  }, [count, fieldValues, tempSearch])

  const baseClasses = useBaseStyles({ smallMedia: isSmall })
  const classes = useStyles({ loaded })

  // Reset pagination page on filter change
  React.useEffect(() => {
    dispatch(setPaginationPage(0))
  }, [allIncludes, fieldValues, url, dispatch])

  // Store loaded tasks in internal cache
  React.useEffect(() => {
    updateCacheFromSWR(allTasks)
  }, [updateCacheFromSWR, allTasks])

  // Store comment counts
  React.useEffect(() => {
    if (allTasks?.data) {
      updateCounts(allTasks.data)
    }
  }, [updateCounts, allTasks])

  // Update mutator
  React.useEffect(() => {
    setMutator(ALL_TAB, 'allTasks', pagination.mutate)
  }, [setMutator, pagination.mutate])

  // Register mutate function to be called when task update happens
  React.useEffect(() => {
    registerCallback(CALLBACK_PHASES.UPDATE, 'AllTasksTab', pagination.mutate)
  }, [registerCallback, pagination.mutate])

  // Update sorting
  React.useEffect(() => {
    // disableSortRemove={true} prevents this from being 0, so dont update state.
    if (tableSort.length === 0) {
      return
    }

    const { normal, custom } = parseSorts(tableSort)
    dispatch(setSort({ normal, custom, tableSort }))
  }, [tableSort, dispatch])

  const setPageIndex = React.useCallback(
    index => {
      dispatch(setPaginationPage(index))
    },
    [dispatch]
  )

  // reset on page size to prevent invalid pages
  const setPageSize = React.useCallback(
    size => {
      dispatch(setPaginationSize(size))
      dispatch(setPaginationPage(0))
    },
    [dispatch]
  )

  const initialSort = React.useMemo(() => [...tableApiSort], [tableApiSort])

  // Account for added ui that changes table height
  const tableHeight = React.useMemo(() => {
    const hasFields = fieldValues.length > 0
    const FIELD_HEIGHT = '44.5px'

    if (showTabs) {
      return hasFields
        ? `calc(100svh - 256px - ${FIELD_HEIGHT})`
        : 'calc(100svh - 256px)'
    }

    return hasFields ? `calc(86svh - ${FIELD_HEIGHT})` : '86svh'
  }, [showTabs, fieldValues])

  const beginSave = React.useCallback(() => {
    setShowModal(true)
  }, [])

  const hideModal = React.useCallback(() => {
    setShowModal(false)
  }, [])

  const clearFilters = React.useCallback(() => {
    clearAll()
    setIncludeSubtasks(false)
    setTempSearch('')
    setFieldValues([])
  }, [clearAll, setIncludeSubtasks, setTempSearch, setFieldValues])

  const fieldFilters = React.useMemo(
    () => (
      <div className={classes.filtersArea}>
        {showSave && (
          <>
            <Tooltip title='Clear Filter' arrow>
              <div className={classes.filtersArea}>
                <Trash
                  onClick={clearFilters}
                  color={theme.palette.primary.main}
                  size={theme.size.base}
                  className={classes.clearIcon}
                />
              </div>
            </Tooltip>

            <Tooltip title='Save Filter' arrow>
              <div className={classes.filtersArea}>
                <SaveAdd
                  onClick={beginSave}
                  color={theme.palette.primary.main}
                  size={theme.size.base}
                  className={classes.saveIcon}
                />
              </div>
            </Tooltip>
          </>
        )}

        <FieldFilters
          limit={TASK_FIELD_LIMIT}
          values={fieldValues}
          setValues={setFieldValues}
          type='task'
        />
      </div>
    ),
    [
      classes.filtersArea,
      clearFilters,
      showSave,
      beginSave,
      theme.palette.primary.main,
      theme.size.base,
      classes.clearIcon,
      classes.saveIcon,
      fieldValues,
      setFieldValues
    ]
  )

  return (
    <>
      <div className={clsx(baseClasses.tasks, classes.root)}>
        <AllTaskTableStateProvider tasks={allTasks} url={url}>
          {/* @ts-expect-error Incorrect prop types */}
          <TaskTable
            id={TABLE_ID}
            pageRef={pageRef}
            loaded={loaded}
            message='There are no tasks matching these filters'
            setTableSort={setTableSort}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
            pageNum={pagination.pageIndex}
            pageSize={pagination.pageSize}
            initialSort={initialSort}
            tableHeight={tableHeight}
            selectable
            enableCustomContextMenu
            customFieldsToInclude={allIncludes}
            {...pagination}
            headerProps={{
              children: !isSmall && (
                <>
                  <Views
                    fieldValues={fieldValues}
                    setFieldValues={setFieldValues}
                    setTempSearch={setTempSearch}
                    hideTitle
                  />

                  <div className={classes.expand}>
                    {showTabs ? (
                      <Maximize4
                        onClick={() => {
                          setShowTabs(false)
                          hideMenu()
                        }}
                      />
                    ) : (
                      <Maximize
                        style={{ transform: 'rotate(90deg)' }}
                        onClick={() => {
                          setShowTabs(true)
                          showMenu()
                        }}
                      />
                    )}
                  </div>
                </>
              )
            }}
          >
            <Table.Header>
              <Table.BottomRow
                isSmall={isSmall}
                className={isSmall ? classes.bottomRow : undefined}
                preChildren={!isSmall && fieldFilters}
              >
                <Filters
                  onUpdate={setURL}
                  tempSearch={tempSearch}
                  setTempSearch={setTempSearch}
                  sort={sort}
                  customSort={customSort}
                  fieldValues={fieldValues}
                  setFieldValues={setFieldValues}
                  hideViews
                >
                  {isSmall && fieldFilters}
                </Filters>
              </Table.BottomRow>

              {fieldValues.length > 0 && (
                <div className={classes.customSearchRow}>
                  <FieldPills
                    advanced
                    values={fieldValues}
                    setValues={setFieldValues}
                  />
                </div>
              )}
            </Table.Header>
          </TaskTable>
        </AllTaskTableStateProvider>
      </div>

      {showModal && (
        <TasksFilterModal
          selected={state}
          selectedFields={fieldValues}
          onClose={hideModal}
          onSave={name => saveFilter({ name, url, onFinish: hideModal })}
        />
      )}

      <Prompt
        when
        message={() => {
          setShowTabs(true)
          dispatch(clearSelected())
          showMenu()
          return true
        }}
      />
    </>
  )
}

export default AllTasksTab
